import React, { ReactElement } from "react";
import { Box, Typography, Chip, useTheme as useMuiTheme } from "@mui/material";
import {
  PictureAsPdf as PdfIcon,
  TableChart as TableChartIcon,
  Code as CodeIcon,
  DataObject as DataObjectIcon,
  Image as ImageIcon,
  VideoFile as VideoFileIcon,
  Audiotrack as AudiotrackIcon,
  Archive as ArchiveIcon,
  Description as DescriptionIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from "@mui/icons-material";
import { getFileTypeFromFilename } from "../helpers/dataSourceUtils";

export interface Citation {
  name: string;
  type: string;
}

// Helper function to get citation icon and color based on file type (matching data sources logic)
const getCitationIconAndColor = (citation: Citation): { icon: ReactElement; color: string } => {
  // First, try to determine file type from citation.type
  let iconType = citation.type?.toLowerCase() || "";
  
  // If type is not specific enough, try to determine from filename
  if (!iconType || iconType === "file" || iconType === "document") {
    iconType = getFileTypeFromFilename(citation.name);
  }

  // Map icon types to icons and colors (matching getDataSourceIcon logic)
  switch (iconType) {
    case "pdf":
      return { icon: <PdfIcon sx={{ fontSize: "16px !important" }} />, color: "#d32f2f" };
    case "csv":
      return { icon: <TableChartIcon sx={{ fontSize: "16px !important" }} />, color: "#2e7d32" };
    case "html":
      return { icon: <CodeIcon sx={{ fontSize: "16px !important" }} />, color: "#1976d2" };
    case "json":
      return { icon: <DataObjectIcon sx={{ fontSize: "16px !important" }} />, color: "#ed6c02" };
    case "image":
      return { icon: <ImageIcon sx={{ fontSize: "16px !important" }} />, color: "#7b1fa2" };
    case "video":
      return { icon: <VideoFileIcon sx={{ fontSize: "16px !important" }} />, color: "#e91e63" };
    case "audio":
      return { icon: <AudiotrackIcon sx={{ fontSize: "16px !important" }} />, color: "#00bcd4" };
    case "archive":
    case "zip":
    case "rar":
      return { icon: <ArchiveIcon sx={{ fontSize: "16px !important" }} />, color: "#ff9800" };
    case "document":
      return { icon: <DescriptionIcon sx={{ fontSize: "16px !important" }} />, color: "#2196f3" };
    case "spreadsheet":
      return { icon: <TableChartIcon sx={{ fontSize: "16px !important" }} />, color: "#4caf50" };
    default:
      return { icon: <InsertDriveFileIcon sx={{ fontSize: "16px !important" }} />, color: "#757575" };
  }
};

interface CitationsDisplayProps {
  citations: Citation[];
  /** Border position: "top" for messages page, "bottom" for compose. Default "bottom". */
  borderPosition?: "top" | "bottom";
}

const CitationsDisplay: React.FC<CitationsDisplayProps> = ({ citations, borderPosition = "bottom" }) => {
  const muiTheme = useMuiTheme();
  const isDarkMode = muiTheme.palette.mode === "dark";

  if (!citations || citations.length === 0) {
    return null;
  }

  const useTopBorder = borderPosition === "top";
  // Messages page (top): smaller pb so gap to usage metrics below isn't excessive when both show
  const spacing = useTopBorder
    ? { mt: 1.5, pt: 1.5, pb: 0.5, borderTop: `1px solid ${muiTheme.palette.divider}` }
    : { mt: 1.5, pt: 1.5, pb: 1.5, borderBottom: `1px solid ${muiTheme.palette.divider}` };

  return (
    <Box
      sx={{
        ...spacing,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 600,
          fontSize: "0.75rem",
        }}
      >
        Sources:
      </Typography>
      {citations.map((citation, index) => {
        const { icon, color } = getCitationIconAndColor(citation);
        return (
          <Chip
            key={index}
            icon={icon}
            label={citation.name}
            size="small"
            sx={{
              backgroundColor: isDarkMode
                ? muiTheme.palette.grey[800]
                : muiTheme.palette.grey[100],
              color: "text.primary",
              fontSize: "0.75rem",
              height: "24px",
              "& .MuiChip-icon": {
                color: `${color} !important`,
              },
            }}
          />
        );
      })}
    </Box>
  );
};

export default CitationsDisplay;
